import { useState } from 'react';
import { Check, ChevronDown, Layers } from 'lucide-react';
import type { VirtualAccountSummary } from '@pnl/shared';

export const CONSOLIDATED = 'consolidated';

interface Props {
  accounts: VirtualAccountSummary[];
  value: string;
  onChange: (value: string) => void;
}

const phaseShort: Record<VirtualAccountSummary['phase'], string> = {
  TEST: 'Em teste',
  FUNDED: 'Funded',
  LIVE: 'Live',
};

interface Option {
  id: string;
  label: string;
  sub: string;
}

/**
 * Dropdown de seleção de conta no topo do dashboard. Mostra cada conta ativa
 * e, quando o trader tem mais de uma, a opção "Consolidado" (soma de todas).
 */
export function AccountSelector({ accounts, value, onChange }: Props) {
  const [open, setOpen] = useState(false);

  const options: Option[] = [
    ...(accounts.length > 1
      ? [{ id: CONSOLIDATED, label: 'Todas as contas', sub: 'Consolidado' }]
      : []),
    ...accounts.map((a) => ({ id: a.id, label: a.label, sub: phaseShort[a.phase] })),
  ];
  const current = options.find((o) => o.id === value) ?? options[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="border-ink-800 bg-ink-900 hover:border-ink-700 flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors"
      >
        <div className="bg-brand-500/15 text-brand-400 grid h-8 w-8 place-items-center rounded-lg">
          <Layers size={15} />
        </div>
        <div className="flex-1">
          <p className="text-2xs text-ink-400 font-semibold uppercase tracking-widest">
            Conta selecionada
          </p>
          <p className="text-ink-100 mt-0.5 text-sm font-semibold">
            {current?.label}
            <span className="text-ink-400 font-normal"> · {current?.sub}</span>
          </p>
        </div>
        <ChevronDown
          size={16}
          className={'text-ink-400 transition-transform ' + (open ? 'rotate-180' : '')}
        />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="border-ink-800 bg-ink-900 absolute inset-x-0 top-full z-30 mt-1 overflow-hidden rounded-xl border shadow-xl">
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
                className={
                  'flex w-full items-center justify-between px-4 py-2.5 text-left text-sm transition-colors ' +
                  (o.id === value
                    ? 'bg-brand-500/15 text-brand-200'
                    : 'text-ink-200 hover:bg-white/5')
                }
              >
                <span>
                  {o.label}
                  <span className="text-ink-500"> · {o.sub}</span>
                </span>
                {o.id === value && <Check size={14} className="text-brand-400" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
